import { sb } from '../supabase';

// Trader side of the factory-thread chat. The TRADER uses the base tables directly
// (RLS: trader_id = auth.uid()). The FACTORY side is masked and goes through
// SECURITY DEFINER RPCs (built in B2). Factory identity is read from the public
// view (base factory_directory isn't buyer-readable).

// Compact, self-contained product reference stored on a message so both sides can
// render a product card without a join. null for a non-product chat.
export function buildProductRef(p) {
  if (!p || !p.id) return null;
  return {
    id: p.id, factory_id: p.factory_id || null,
    name_ar: p.name_ar || null, name_en: p.name_en || null,
    image: p.image || null, ref_code: p.ref_code || null,
    price: p.price || null, currency: p.currency || null,
  };
}


// One thread + its factory's public identity.
export async function fetchTraderThread(threadId) {
  const { data: t, error } = await sb.from('factory_threads')
    .select('id, factory_id, share_slug, last_message_at, created_at')
    .eq('id', threadId).maybeSingle();
  if (error) throw error;
  if (!t) return null;
  const { data: f } = await sb.from('factory_directory_public')
    .select('id, company_name, company_name_latin, city, country, profile_image, category')
    .eq('id', t.factory_id).maybeSingle();
  return { ...t, factory: f || null };
}

// Messages in a thread, chronological.
export async function fetchTraderMessages(threadId) {
  const { data, error } = await sb.from('factory_thread_messages')
    .select('id, sender_role, content, product_ref, created_at')
    .eq('thread_id', threadId).order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// Mark the factory's messages as read by the trader.
export async function markTraderRead(threadId) {
  await sb.from('factory_thread_messages')
    .update({ read_by_trader: true })
    .eq('thread_id', threadId).eq('sender_role', 'factory').eq('read_by_trader', false);
}



// ── Factory side (masked) — everything goes through SECURITY DEFINER RPCs so the
// factory never touches the base tables and never sees the trader's identity. ──

// Anonymous preview of a share link: factory identity + claim state. No trader data.
export async function getFactoryThreadInvite(slug) {
  const { data, error } = await sb.rpc('get_factory_thread_invite', { p_slug: slug });
  if (error) throw error;
  return (data && data[0]) || null;
}

// Bind the (now authenticated) user to the factory on first claim, else enter.
// Returns the thread id; raises if the factory is claimed by another account.
export async function claimAndEnterThread(slug) {
  const { data, error } = await sb.rpc('claim_and_enter_thread', { p_slug: slug });
  if (error) throw error;
  return data;
}

// The trader on the other side of a thread this caller's factory owns.
//
// The claim flow has to land the factory in the ONE conversation that brought
// it — the trader who sent the request — and the unified surface addresses that
// as /chat/f/:factoryId/:traderId. Nothing else can supply the trader id:
// get_factory_thread_invite omits it (it is shown pre-auth), RLS on
// factory_threads is trader + admin only, and claim_and_enter_thread returns
// the thread id alone. Its signature is deliberately NOT changed — the mobile
// app may call it.
//
// Returns null when the caller does not own the factory, which the caller
// treats as "no deep link" rather than an error.
export async function getOwnerThreadPeer(threadId) {
  if (!threadId) return null;
  const { data, error } = await sb.rpc('get_owner_thread_peer', { p_thread_id: threadId });
  if (error) return null;
  return data || null;
}

// Factory reads a thread (marks trader messages read); trader identity omitted.
export async function fetchFactoryThreadMessages(threadId) {
  const { data, error } = await sb.rpc('get_factory_thread_messages', { p_thread_id: threadId });
  if (error) throw error;
  return data || [];
}

// Factory sends a reply.
export async function sendFactoryThreadMessage(threadId, content) {
  const body = (content || '').trim();
  if (!body) throw new Error('empty');
  const { data, error } = await sb.rpc('send_factory_thread_message', { p_thread_id: threadId, p_content: body });
  if (error) throw error;
  return data;
}


// ── Admin oversight (not masked — sees trader identity + share link) ─────────
//
// Built from the base tables rather than an RPC. The RPC this replaces,
// admin_list_factory_threads, was written in 20260804000002 and NEVER APPLIED to
// production — pg_get_functiondef returns nothing for it — so this page has been
// rendering a PGRST202 error rather than a list. Creating the missing function
// only to point it at the retired table would be two migrations to arrive where
// a plain query already works: admins hold is_admin_user() on the messages
// select policy (20260813000008), so they can read every row directly.
//
// Counts every message in both directions. The admin is watching operational
// health — whether factories reply at all, and how many conversations are
// actually moving — so a one-sided count would answer the wrong question.
export async function adminListFactoryThreads() {
  const { data: threads, error } = await sb.from('factory_threads')
    .select('id, factory_id, trader_id, share_slug, created_at, last_message_at')
    .order('last_message_at', { ascending: false });
  if (error) throw error;
  if (!threads?.length) return [];

  const facIds = [...new Set(threads.map((t) => t.factory_id).filter(Boolean))];
  const traderIds = [...new Set(threads.map((t) => t.trader_id).filter(Boolean))];

  const [{ data: facs }, { data: profs }, { data: msgs }] = await Promise.all([
    sb.from('factory_directory').select('id, company_name, company_name_latin').in('id', facIds),
    sb.from('profiles').select('id, full_name, company_name, phone').in('id', traderIds),
    // Ascending so the last row per thread wins the reduce below, which is the
    // same trick fetchMyBuyerThreads uses rather than a per-thread query.
    sb.from('messages').select('thread_id, sender_id, content, created_at, is_read')
      .not('thread_id', 'is', null).order('created_at', { ascending: true }),
  ]);

  const fm = Object.fromEntries((facs || []).map((f) => [f.id, f]));
  const pm = Object.fromEntries((profs || []).map((p) => [p.id, p]));

  // One pass over the messages, not one filter per thread: with 90 factories
  // about to be invited this list grows on both axes at once.
  const traderOf = Object.fromEntries(threads.map((t) => [t.id, t.trader_id]));
  const agg = {};
  for (const m of (msgs || [])) {
    const a = agg[m.thread_id] || (agg[m.thread_id] = { last: null, count: 0, unread: 0 });
    a.last = m;               // ascending, so the final assignment is the latest
    a.count += 1;
    // "Unread by the factory" is a trader message the owner has not opened.
    // sender_role went with the legacy table; the trader is known from the
    // thread, so the role is derived rather than stored.
    if (m.sender_id === traderOf[m.thread_id] && !m.is_read) a.unread += 1;
  }

  return threads.map((t) => {
    const f = fm[t.factory_id] || {};
    const p = pm[t.trader_id] || {};
    const a = agg[t.id] || { last: null, count: 0, unread: 0 };
    return {
      thread_id: t.id,
      share_slug: t.share_slug,
      factory_name: (f.company_name_latin || '').trim() || f.company_name || '',
      trader_name: p.full_name || '',
      trader_company: p.company_name || '',
      trader_phone: p.phone || '',
      last_preview: a.last?.content || '',
      last_sender_role: a.last ? (a.last.sender_id === t.trader_id ? 'trader' : 'factory') : null,
      last_message_at: a.last?.created_at || t.last_message_at,
      unread_by_factory: a.unread,
      message_count: a.count,
    };
  });
}

// One thread's header (factory + trader identity + share_slug) for the admin view.
export async function adminFetchThread(threadId) {
  const { data: t, error } = await sb.from('factory_threads')
    .select('id, factory_id, trader_id, share_slug, created_at, last_message_at')
    .eq('id', threadId).maybeSingle();
  if (error) throw error;
  if (!t) return null;
  const [{ data: f }, { data: p }] = await Promise.all([
    sb.from('factory_directory').select('id, company_name, company_name_latin, city, country, profile_image, linked_supplier_id').eq('id', t.factory_id).maybeSingle(),
    t.trader_id
      ? sb.from('profiles').select('id, full_name, company_name, email, phone').eq('id', t.trader_id).maybeSingle()
      : Promise.resolve({ data: null }),
  ]);
  return { ...t, factory: f || null, trader: p || null };
}

// A thread's messages for the admin (read-only; admin RLS allows all threads).
//
// Reads `messages` by thread_id. sender_role is derived from the thread's
// trader rather than stored: the unified table records who sent a message, and
// a thread has exactly one trader, so the role follows. Kept in the returned
// shape because the admin views render it as a label.
export async function adminFetchThreadMessages(threadId, traderId = null) {
  let trader = traderId;
  if (!trader) {
    const { data: t } = await sb.from('factory_threads').select('trader_id').eq('id', threadId).maybeSingle();
    trader = t?.trader_id || null;
  }
  const { data, error } = await sb.from('messages')
    .select('id, sender_id, content, product_ref, created_at, is_read')
    .eq('thread_id', threadId).order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((m) => ({
    ...m,
    sender_role: m.sender_id === trader ? 'trader' : 'factory',
  }));
}
